/**
 * Cross-app SSO utilities
 * Provides token handoff between AMS and FMA apps
 * 
 * Environment Variables:
 * - VITE_AMS_APP_URL: AMS app URL (default: http://localhost:5173)
 * - VITE_FMA_APP_URL: FMA app URL (default: http://localhost:5174)
 */
import { supabase } from '@/lib/supabase'

export interface AppConfig {
  ams: string
  fma: string
}

// Get app URLs from environment variables with fallbacks
function getAppUrls(): AppConfig {
  return {
    ams: (import.meta as any).env?.VITE_AMS_APP_URL || 'http://localhost:5173',
    fma: (import.meta as any).env?.VITE_FMA_APP_URL || 'http://localhost:5174',
  }
}

/**
 * Get current app URLs configuration
 */
export function getAppConfig(): AppConfig {
  return getAppUrls()
}

/**
 * Redirects user to another app with current session tokens
 * Uses environment-configured URLs by default
 */
export async function redirectToOtherAppWithToken(
  targetApp: keyof AppConfig, 
  appUrls: AppConfig = getAppUrls(),
  redirectPath: string = '/'
) {
  const { data: { session } } = await supabase.auth.getSession()
  
  if (!session) {
    throw new Error('No active session to transfer')
  }

  const targetUrl = appUrls[targetApp]
  const consumeUrl = new URL('/auth/consume', targetUrl)
  
  // Add tokens as URL hash for security (not logged in server logs)
  consumeUrl.hash = `access_token=${session.access_token}&refresh_token=${session.refresh_token}&redirect_to=${encodeURIComponent(redirectPath)}`
  
  // Navigate to the other app
  window.location.href = consumeUrl.toString()
}

/**
 * Consumes tokens from URL and sets up session
 * Should be called on the /auth/consume route
 */
export async function consumeTokensFromUrl(): Promise<string> {
  const hash = window.location.hash.substring(1)
  const params = new URLSearchParams(hash)
  
  const accessToken = params.get('access_token')
  const refreshToken = params.get('refresh_token')
  const redirectTo = params.get('redirect_to') || '/'
  
  if (!accessToken) {
    throw new Error('No access token found in URL')
  }

  // Set the session using the provided tokens
  // Only include refresh_token if it exists and is not empty
  const sessionData: { access_token: string; refresh_token?: string } = refreshToken && refreshToken.trim() !== '' 
    ? { access_token: accessToken, refresh_token: refreshToken }
    : { access_token: accessToken }
  
  const { error } = await supabase.auth.setSession(sessionData as any)

  if (error) {
    throw new Error(`Failed to set session: ${error.message}`)
  }

  // Clear the URL hash for security
  window.history.replaceState(null, '', window.location.pathname)
  
  return redirectTo
}

/**
 * React hook for handling SSO handoff
 * Uses environment-configured URLs
 */
export function useAppHandoff() {
  const appUrls = getAppUrls()
  return {
    goToAMS: (path?: string) => redirectToOtherAppWithToken('ams', appUrls, path),
    goToFMA: (path?: string) => redirectToOtherAppWithToken('fma', appUrls, path),
  }
}
